<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import { router } from '@inertiajs/svelte';
    import DataTable from '../../components/DataTable.svelte';
    import { show } from '@/routes/admin/users';
    import { users as usersRoute } from '@/routes/admin';
    import { formatDate } from '../../utils/dates';

    interface RoleInfo {
        value: string;
        label: string;
        description: string;
        color: string;
        icon: string;
    }

    interface User {
        id: number;
        name: string;
        email: string;
        roles: RoleInfo[];
        created_at: string;
    }

    interface AvailableRole {
        value: string;
        label: string;
        color: string;
        icon: string;
    }

    const colorClasses: Record<string, { badge: string; filter: string; filterActive: string }> = {
        purple: { badge: 'bg-purple-900 text-purple-300', filter: 'bg-purple-900 text-purple-300 hover:bg-purple-800', filterActive: 'bg-purple-600 text-white ring-2 ring-purple-400 ring-offset-1 ring-offset-gray-950' },
        blue: { badge: 'bg-cyan-900 text-cyan-300', filter: 'bg-cyan-900 text-cyan-300 hover:bg-cyan-800', filterActive: 'bg-cyan-600 text-white ring-2 ring-cyan-400 ring-offset-1 ring-offset-gray-950' },
        green: { badge: 'bg-green-900 text-green-300', filter: 'bg-green-900 text-green-300 hover:bg-green-800', filterActive: 'bg-green-600 text-white ring-2 ring-green-400 ring-offset-1 ring-offset-gray-950' },
        gray: { badge: 'bg-gray-800 text-gray-300', filter: 'bg-gray-800 text-gray-300 hover:bg-gray-700', filterActive: 'bg-gray-600 text-white ring-2 ring-gray-400 ring-offset-1 ring-offset-gray-950' },
    };

    let { users = [], totalCount = null, sort = 'id', direction = 'desc', role = '', availableRoles = [] }: { users: User[]; totalCount: number | null; sort: string; direction: 'asc' | 'desc'; role: string; availableRoles: AvailableRole[] } = $props();

    function handleSort(key: string, dir: 'asc' | 'desc') {
        router.visit(usersRoute({ mergeQuery: { sort: key, direction: dir } }), {
            preserveScroll: true,
            preserveState: true,
        });
    }

    function filterByRole(value: string) {
        const next = role === value ? null : (value || null);
        router.visit(usersRoute({ mergeQuery: { role: next } }), {
            preserveScroll: true,
            preserveState: true,
        });
    }

    const columns = [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: null, label: 'Roles' },
        { key: 'created_at', label: 'Joined' },
    ];
</script>

<svelte:head>
    <title>Users — Admin — Case of the Day</title>
</svelte:head>

<div class="flex items-center justify-between mb-6">
    <h1 class="text-2xl font-bold text-gray-100">Users</h1>
        <div class="flex items-center gap-2">
            <button
                onclick={() => filterByRole('')}
                class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors {!role ? 'bg-gray-100 text-gray-900 ring-2 ring-white ring-offset-1 ring-offset-gray-950' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}"
            >
                All
            </button>
            {#each availableRoles as r (r.value)}
                <button
                    onclick={() => filterByRole(r.value)}
                    class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors {role === r.value ? colorClasses[r.color].filterActive : colorClasses[r.color].filter}"
                >
                    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                    {@html r.icon.replace('<svg', '<svg class="size-3"')}
                    {r.label}
                </button>
            {/each}
        </div>
    </div>

<DataTable
    {columns}
    items={users}
    {sort}
    {direction}
    onsort={handleSort}
    onrowclick={(user) => router.visit(show.url(user.id))}
    {totalCount}
    onclearfilters={role ? () => filterByRole('') : undefined}
>
    {#snippet row(user)}
        <td class="px-6 py-4 text-sm text-gray-500">{user.id}</td>
        <td class="px-6 py-4 text-sm text-gray-100">{user.name}</td>
        <td class="px-6 py-4 text-sm text-gray-400">{user.email}</td>
        <td class="px-6 py-4 text-sm text-gray-500">
            {#if user.roles.length > 0}
                <div class="flex gap-1 flex-wrap">
                    {#each user.roles as role (role.value)}
                        <span
                            class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium {colorClasses[role.color].badge}"
                            title={role.description}
                        >
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            {@html role.icon.replace('<svg', '<svg class="size-3"')}
                            {role.label}
                        </span>
                    {/each}
                </div>
            {:else}
                <span class="text-gray-400">—</span>
            {/if}
        </td>
        <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDate(user.created_at)}</td>
    {/snippet}
</DataTable>
